import React, { HTMLAttributeAnchorTarget, memo, ReactNode, useCallback, useMemo } from 'react';
import { DOMProps } from '@react-types/shared';
import { useLogger, LoggableProps } from '@direct-frontend/modules-logger';

import { cn } from '../../lib/css/className/className';
import './MicroNotification.css';
import { TestProps } from '../../internal/types/testTypes';
import { useHStack } from '../../hooks/useStack';
import {
    IconAlertFilled,
    IconBlock,
    IconCloseSkinny,
    IconErrorFilled,
    IconInfoFilled,
    IconSelectFilled,
} from '../../icons/colorless';
import { Spinner } from '../Spinner';
import { useInvertedTheme, useTheme } from '../../lib/theme/ThemeProvider';
import { Button } from '../Button';
import { ClickableIcon } from '../ClickableIcon';
import { TextPresetWithSkin } from '../TextGeneric/types';
import { useInternalVerticalAlignWithSkin } from '../../hooks/useVerticalAlign/useVerticalAlignWithSkin';
import { getSizeSkin } from '../../lib/skin/getSizeSkin/getSizeSkin';
import { TextGenericWithSkin } from '../TextGeneric/TextGenericWithSkin';
import { IconSize } from '../../icons';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

import { i18n } from './MicroNotification.i18n';

const cls = cn('MicroNotification');

export type MicroNotificationType = 'message' | 'info' | 'warning' | 'error' | 'success' | 'pending' | 'block';

export type MicroNotificationButtonOptions = {
    /**
     * Текст на кнопке
     */
    text: string;

    /**
     * Обработчик нажатия на кнопку
     */
    onClick?: () => void;

    /**
     * HTML-атрибут href для кнопки, при его передаче кнопка становится ссылкой
     */
    href?: string;

    /**
     * HTML-атрибут target для ссылки
     */
    target?: HTMLAttributeAnchorTarget;

    /**
     * HTML-атрибут rel для ссылки
     */
    rel?: string;
};

export interface MicroNotificationPropsRaw extends TestProps, DOMProps {
    /**
     * Основной текст (заголовок или абзац)
     */
    children: ReactNode;

    /**
     * Дополнительный поясняющий текст (описание к заголовку)
     */
    caption?: ReactNode;

    /**
     * Тип уведомления
     * @default message
     */
    type?: MicroNotificationType;

    /**
     * Действие, выполняемое при закрытии.
     * При передаче параметра появляется кнопка-крестик.
     */
    onClose?: () => void;

    /**
     * Текст (title) для кнопки-крестика для закрытия компонента
     * @default Закрыть
     */
    closeButtonText?: string;

    /**
     * Свойства кнопки
     */
    buttonProps?: MicroNotificationButtonOptions;

    /**
     * Ширина контейнера:
     * auto — по контенту содержимого, значение по умолчанию.
     * max — на всю ширину контейнера.
     * @default auto
     */
    width?: 'auto' | 'max';

    /**
     * Названия классов для элемента
     */
    className?: string;
}

export type MicroNotificationProps = LoggableProps<MicroNotificationPropsRaw>;

/**
 * Получение иконки по типу уведомления
 */
const icons: Record<MicroNotificationType, (iconSize: IconSize) => ReactNode> = {
    error: iconSize => <IconErrorFilled size={iconSize} className={cls('icon', { color: 'error' })} />,
    warning: iconSize => <IconAlertFilled size={iconSize} className={cls('icon', { color: 'warning' })} />,
    block: iconSize => <IconBlock size={iconSize} className={cls('icon')} />,
    info: iconSize => <IconInfoFilled size={iconSize} className={cls('icon', { color: 'info' })} />,
    success: iconSize => <IconSelectFilled size={iconSize} className={cls('icon', { color: 'success' })} />,
    pending: iconSize => <Spinner size={iconSize} className={cls('icon')} />,
    message: () => null,
};

const microNotificationCloseButtonNode = { name: 'MicroNotificationCloseButton' };

/**
 * Компонент для показа уведомлений
 */
export const MicroNotification = memo<MicroNotificationProps>(function MicroNotification({
    children,
    caption,
    type = 'message',
    onClose,
    closeButtonText,
    buttonProps,
    width = 'auto',
    className,
    'data-testid': dataTestId = 'MicroNotification',
    ...props
}) {
    // логирование при клике на кнопку и показе
    const { LogProvider, logClick } = useLogger('MicroNotification', props.logNode);

    const invertedTheme = useInvertedTheme();

    const skinSize = 'm';
    const sizeSkinClass = getSizeSkin(skinSize);
    const stackClasses = useHStack({ alignItems: 'flex-start', gap: 'current-geometry' });

    const textSize: TextPresetWithSkin = caption ? 'current-main + current-body' : 'current-main';
    const icon = icons[type];

    const { text: buttonText, onClick, ...otherButtonProps } = buttonProps ?? {};

    const onButtonClickHandler = useCallback(() => {
        logClick();
        onClick?.();
    }, [logClick, onClick]);

    const button = useMemo(() => {
        if (!buttonText) {
            return;
        }

        return (
            <Button
                color="normal"
                size="xs"
                className={cls('button')}
                onClick={onButtonClickHandler}
                title={buttonText}
                data-testid={`${dataTestId}.Button`}
                {...otherButtonProps}
            >
                {buttonText}
            </Button>
        );
    }, [buttonText, onButtonClickHandler, dataTestId, otherButtonProps]);

    const { iconSize } = useTheme().sizeTokens[skinSize];
    const { addonClasses } = useInternalVerticalAlignWithSkin({ textSize: textSize, addonType: 'icon' });
    closeButtonText = closeButtonText || i18n('Закрыть');

    const skinClass = getColorSkin('gray-neutral', {});

    return (
        <LogProvider>
            <div
                id={props.id}
                className={cls(
                    {
                        width,
                        'has-button': Boolean(buttonProps),
                    },
                    [className, stackClasses, invertedTheme, sizeSkinClass, skinClass],
                )}
                data-testid={`${dataTestId}.${type}`}
                role="alert"
            >
                {icon(iconSize)}

                <TextGenericWithSkin
                    className={cls('text')}
                    textSize={textSize}
                    skinSize={skinSize}
                    color="current"
                    caption={caption}
                >
                    {children}
                </TextGenericWithSkin>

                {button}

                {onClose && (
                    <ClickableIcon
                        className={cls('closeButton', [addonClasses])}
                        onClick={onClose}
                        title={closeButtonText}
                        aria-label={closeButtonText}
                        data-testid={`${dataTestId}.CloseButton`}
                        logNode={microNotificationCloseButtonNode}
                    >
                        <IconCloseSkinny size={iconSize} />
                    </ClickableIcon>
                )}
            </div>
        </LogProvider>
    );
});
